import React, { ReactNode, useCallback, useEffect } from "react";

import GuideStatus from "@mapmycustomers/shared/enum/guides/GuideStatus";
import { Modal } from "@mapmycustomers/ui";

import ChecklistDescriptor, { ChecklistStep } from "@app/component/flows/types/ChecklistDescriptor";

import styles from "./Checklist.module.scss";
import ChecklistItem from "./ChecklistItem";
import ChecklistItemType from "./types/ChecklistItemType";

export type CompletionStatus = Record<ChecklistItemType["id"], boolean>;

export interface ChecklistProps {
  completionStatus?: CompletionStatus;
  id: ChecklistDescriptor["id"];
  items?: ChecklistItemType[];
  onHide?: (slug: string) => void;
  onItemClick?: (slug: ChecklistDescriptor["id"], itemId: ChecklistStep["id"]) => void;
  onStatusChange?: (status: GuideStatus, force?: boolean) => void;
  subTitle?: ReactNode;
  title?: ReactNode;
  visible?: boolean;
}

const Checklist = ({
  id,
  completionStatus,
  items,
  onHide,
  onItemClick,
  onStatusChange,
  subTitle,
  title,
  visible,
}: ChecklistProps) => {
  useEffect(() => {
    const allDone = items?.every((item) => !!completionStatus?.[item.id]);
    if (allDone) {
      onStatusChange?.(GuideStatus.COMPLETED);
    } else {
      onStatusChange?.(visible ? GuideStatus.PROGRESS : GuideStatus.NOT_STARTED);
    }
  }, [visible, items, onStatusChange, completionStatus, id, onHide]);

  const handleCancel = useCallback(() => {
    onHide?.(id);
  }, [id, onHide]);

  const handleItemClick = useCallback(
    (itemId: ChecklistItemType["id"]) => onItemClick?.(id, itemId),
    [id, onItemClick]
  );

  return (
    <Modal
      className={styles.modal}
      footer={null}
      onCancel={handleCancel}
      open={visible}
      title={title}
      width="30rem"
    >
      {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
      {items?.map((item) => (
        <ChecklistItem
          complete={completionStatus?.[item.id]}
          item={item}
          key={item.id}
          onClick={handleItemClick}
        />
      ))}
    </Modal>
  );
};

export default Checklist;
